import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '@/context/AuthContext';
import {api, errMsg} from '@/lib/api';
import {currencyCode, currencyLabel} from '@/lib/money';
import SettingsSection from '@/components/SettingsSection';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {toast} from 'sonner';

const ENDPOINT = '/loyalty/birthday-settings';

export default function BirthdayBonusSettings({compact=false}) {
  const {t, i18n} = useTranslation();
  const {user, studio, workspace, entitlements} = useAuth();
  const role = workspace?.role || user?.role;
  const identity = `${user?.id || ''}:${workspace?.scope?.studio_id || studio?.id || ''}:${role || ''}`;
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const savedIdentity = useRef(identity);
  savedIdentity.current = identity;
  const text = (key, values) => t(`birthdayBonus.${key}`, values);

  useEffect(() => {
    const request = ++generation.current;
    setForm(null); setError(''); setSaving(false);
    if (role !== 'studio_owner') return;
    api.get(ENDPOINT).then(result => {
      if (request === generation.current && savedIdentity.current === identity) setForm(result);
    }).catch(cause => {
      if (request === generation.current && savedIdentity.current === identity) setError(errMsg(cause));
    });
    return () => { generation.current += 1; };
  }, [identity, role, reload]);

  if (role !== 'studio_owner') return null;
  const change = (key, value) => setForm(current => ({...current, [key]:value}));
  const save = async event => {
    event.preventDefault();
    if (!form || saving || entitlements?.read_only || savedIdentity.current !== identity) return;
    const value = Number(form.value), days = Number(form.valid_days);
    if (!form.label?.trim() || !Number.isFinite(value) || value < 0 || value > 10000 ||
        (form.enabled && value <= 0) || !Number.isInteger(days) || days < 1 || days > 365) {
      setError(text('invalid')); return;
    }
    const request = generation.current;
    const ownerIdentity = identity;
    setSaving(true); setError('');
    try {
      const result = await api.put(ENDPOINT, {enabled:Boolean(form.enabled), label:form.label.trim(),
        value, valid_days:days, leap_day:form.leap_day, revision:form.revision});
      if (request !== generation.current || savedIdentity.current !== ownerIdentity) return;
      setForm(result); toast.success(text('saved'));
      window.dispatchEvent(new Event('tps:data-changed'));
    } catch (cause) {
      if (request === generation.current && savedIdentity.current === ownerIdentity) setError(errMsg(cause));
    } finally {
      if (request === generation.current && savedIdentity.current === ownerIdentity) setSaving(false);
    }
  };
  const status = form?.status;
  const Wrapper = compact ? 'div' : SettingsSection;
  return <Wrapper {...(compact ? {className:'tps-birthday-compact'} : {id:'birthday-bonus',title:text('title'),subtitle:text('subtitle'),status:form ? <span className="text-xs">{text(form.enabled ? 'active' : 'inactive')}</span> : null})}>
    {error && <div role="alert" className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive">{error}
      <Button variant="outline" size="sm" className="ml-3" disabled={saving} onClick={() => setReload(value => value + 1)}>{text(form ? 'reload' : 'retry')}</Button>
    </div>}
    {!form && !error && <p role="status" className="text-sm text-muted-foreground">{text('loading')}</p>}
    {form && <form onSubmit={save} className="space-y-3">
      <fieldset disabled={saving||entitlements?.read_only} className="space-y-3 disabled:opacity-70">
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-1" checked={Boolean(form.enabled)} onChange={event => change('enabled', event.target.checked)}/>
          <span><b>{text('enabled')}</b>{!compact&&<span className="mt-1 block text-xs text-muted-foreground">{text('audience')}</span>}</span>
        </label>
        {form.enabled && <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">{text('label')}<Input className="mt-1.5" required maxLength={120} value={form.label} onChange={event => change('label', event.target.value)}/></label>
          <label className="text-sm">{text('value')} {currencyLabel(currencyCode(studio))}<Input className="mt-1.5" type="number" required min="0.01" max="10000" step="0.01" value={form.value} onChange={event => change('value', event.target.value)}/></label>
          <label className="text-sm">{text('days')}<Input className="mt-1.5" type="number" required min="1" max="365" step="1" value={form.valid_days} onChange={event => change('valid_days', event.target.value)}/></label>
          <label className="text-sm sm:col-span-3">{text('leap')}<select className="mt-1.5 h-10 w-full rounded-md border bg-background px-3 text-sm" value={form.leap_day} onChange={event => change('leap_day', event.target.value)}>
            {['feb28','mar1','leap_years'].map(rule => <option key={rule} value={rule}>{text(rule)}</option>)}
          </select></label>
          <p className="text-xs text-muted-foreground sm:col-span-3">{text('expiry')}</p>
        </div>}
        {compact ? <details className="rounded-lg border p-3 text-xs text-muted-foreground"><summary className="cursor-pointer font-medium text-foreground">{text('rules')}</summary><div className="mt-2 space-y-2"><p>{text('audience')}</p><p>{text('limits')}</p></div></details> : <p className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">{text('limits')}</p>}
        <div className="flex justify-end"><Button type="submit" size="sm">{text(saving ? 'saving' : 'save')}</Button></div>
      </fieldset>
      {status ? <div className="text-xs text-muted-foreground"><p>{text('last')}: {new Date(status.checked_at).toLocaleString(i18n.language)}</p><p>{text('report', {awarded:status.awarded || 0, missing:status.missing_date || 0, quota:status.quota_blocked || 0, failed:status.failed || 0})}</p></div> : <p className="text-xs text-muted-foreground">{text('never')}</p>}
    </form>}
  </Wrapper>;
}
